<template>
  <a-modal
    :visible="props.visible"
    :title="getModalTitle"
    :footer="null"
    width="800px"
    centered
    destroy-on-close
    wrap-class-name="enterprise-wechat-record-modal"
    @update:visible="handleVisibleChange"
    @cancel="handleClose"
  >
    <a-spin :spinning="loading && messageList.length === 0">
      <div
        ref="listRef"
        class="record-list"
        @touchstart.passive="handleTouchStart"
        @touchend.passive="handleTouchEnd"
        @wheel="handleWheel"
        @scroll="handleScroll"
      >
        <div v-show="refreshing" class="refresh-overlay">刷新中...</div>
        <template v-if="messageList.length">
          <div
            v-for="(item, index) in messageList"
            :key="getMessageKey(item, index)"
            class="record-item"
          >
            <img class="avatar" :src="item.sendAvatar" alt="" />
            <div class="record-body">
              <div class="record-meta">
                <span class="sender">{{ formatSenderLabel(item) }}</span>
                <span class="time">{{ formatMsgTime(item.sendTime) }}</span>
              </div>
              <WeComMessageContent :message="item" @preview-image="handlePreviewImage" />
            </div>
          </div>
          <div v-if="loading && !refreshing" class="load-more-tip">加载中...</div>
          <div v-else-if="!hasMore" class="load-more-tip">没有更多了</div>
          <div ref="bottomSentinel" class="scroll-sentinel" />
        </template>
        <div v-else-if="!loading" class="empty-tip">暂无聊天记录</div>
      </div>
    </a-spin>

    <ImageViewer
      :visible="imageViewerVisible"
      :image-list="imagePreviewList"
      :current-id="previewImgId"
      @setVisible="setImageViewerVisible"
    />
  </a-modal>
</template>

<script setup>
import { computed, ref, watch, defineProps, defineEmits, nextTick, onBeforeUnmount, h } from 'vue'
import {
  formatMsgTime,
  formatSenderLabel,
  getItemMediaUrl,
  getMessageKey,
  isPreviewableImage,
  parseHasNext
} from './utils'
import { getGroupForwardDetail } from '@/api/workPlace/enterpriseWeChatRecord'
import ImageViewer from '@/views/WorkPlace/Container/chat/components/ImageViewer/index.vue'
import WeComMessageContent from './WeComMessageContent.vue'

const PAGE_SIZE = 20
const SCROLL_BOTTOM_THRESHOLD = 80
const PULL_REFRESH_DISTANCE = 50
const WHEEL_REFRESH_DELTA = 80
const REFRESH_COOLDOWN_MS = 600

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  recordData: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:visible'])

const listRef = ref(null)
const modalTitle = ref('')
const modalTime = ref('')
const bottomSentinel = ref(null)
const messageList = ref([])
const loading = ref(false)
const refreshing = ref(false)
const hasMore = ref(true)
const pageNo = ref(1)
const previewImgId = ref('')
const imageViewerVisible = ref(false)

const getModalTitle = () => {
  return h('div', { class: 'modal-title' }, [
    h('span', { class: 'modal-title-main' }, modalTitle.value),
    modalTime.value ? h('span', { class: 'modal-title-range' }, `(${modalTime.value})`) : null
  ])
}

let bottomObserver = null
let touchStartY = 0
let wheelPullDelta = 0
let refreshCooldown = false

const imagePreviewList = computed(() =>
  messageList.value.filter(isPreviewableImage).map(item => ({
    url: getItemMediaUrl(item),
    msgId: item.msgId
  }))
)

const handlePreviewImage = item => {
  previewImgId.value = item.msgId
  imageViewerVisible.value = true
}

const setImageViewerVisible = status => {
  imageViewerVisible.value = status
  if (!status) previewImgId.value = ''
}

const resetState = () => {
  messageList.value = []
  pageNo.value = 1
  hasMore.value = true
  previewImgId.value = ''
  imageViewerVisible.value = false
  modalTitle.value = ''
  modalTime.value = ''
}

const mergeMessages = (list = []) => {
  if (pageNo.value === 1) {
    messageList.value = list || []
    return
  }
  const existKeys = new Set(messageList.value.map((item, index) => getMessageKey(item, index)))
  ;(list || []).forEach((item, index) => {
    const key = getMessageKey(item, index)
    if (!existKeys.has(key)) {
      messageList.value.push(item)
    }
  })
}

const fetchMessages = async ({ autoLoadMore = false } = {}) => {
  const { weComGroupId, startSeq, endSeq } = props.recordData || {}
  if (!weComGroupId || startSeq == null || endSeq == null) {
    hasMore.value = false
    return
  }
  loading.value = true
  try {
    const res = await getGroupForwardDetail({
      // weComGroupId: 'wrU_xXCgAAiuS67EoUtsrtBWFXVMWN9A',
      // startSeq: 4647,
      // endSeq: 10000,
      weComGroupId,
      startSeq,
      endSeq,
      pageNo: pageNo.value,
      pageSize: PAGE_SIZE
    })
    mergeMessages(res?.chatRecordItemList || [])
    hasMore.value = parseHasNext(res?.hasNext)
    if (pageNo.value === 1) {
      modalTitle.value = res?.title || ''
      modalTime.value = res?.chatTimeRangeText || ''
    }
  } catch (e) {
    if (pageNo.value === 1) {
      messageList.value = []
    }
    hasMore.value = false
    console.error('企微聊天记录加载失败:', e)
  } finally {
    loading.value = false
    if (autoLoadMore) {
      await nextTick()
      tryFillViewport()
    }
  }
}

const refreshMessages = async () => {
  if (loading.value || refreshing.value || refreshCooldown) return
  refreshCooldown = true
  refreshing.value = true
  teardownBottomObserver()
  wheelPullDelta = 0
  pageNo.value = 1
  hasMore.value = true
  try {
    await fetchMessages({ autoLoadMore: false })
  } finally {
    refreshing.value = false
    await nextTick()
    setupBottomObserver()
    tryFillViewport()
    setTimeout(() => {
      refreshCooldown = false
    }, REFRESH_COOLDOWN_MS)
  }
}

const loadMore = async () => {
  if (loading.value || refreshing.value || !hasMore.value) return
  pageNo.value += 1
  await fetchMessages({ autoLoadMore: true })
}

const isBottomReached = el => {
  if (!el) return false
  const { scrollTop, scrollHeight, clientHeight } = el
  return scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_THRESHOLD
}

const tryFillViewport = () => {
  if (!listRef.value || !hasMore.value || loading.value || refreshing.value) return
  if (!messageList.value.length) return
  if (isBottomReached(listRef.value)) {
    loadMore()
  }
}

const handleScroll = e => {
  if (isBottomReached(e.target)) {
    loadMore()
  }
}

const setupBottomObserver = () => {
  teardownBottomObserver()
  if (!listRef.value || !bottomSentinel.value) return
  bottomObserver = new IntersectionObserver(
    entries => {
      if (
        entries[0]?.isIntersecting &&
        hasMore.value &&
        !loading.value &&
        !refreshing.value &&
        messageList.value.length
      ) {
        loadMore()
      }
    },
    { root: listRef.value, threshold: 0 }
  )
  bottomObserver.observe(bottomSentinel.value)
}

const teardownBottomObserver = () => {
  bottomObserver?.disconnect()
  bottomObserver = null
}

const handleTouchStart = e => {
  if (listRef.value?.scrollTop > 0) return
  touchStartY = e.touches[0].pageY
}

const handleTouchEnd = e => {
  if (listRef.value?.scrollTop > 0) return
  const delta = e.changedTouches[0].pageY - touchStartY
  if (delta > PULL_REFRESH_DISTANCE) {
    refreshMessages()
  }
}

const handleWheel = e => {
  const el = listRef.value
  if (!el || el.scrollTop > 0) {
    wheelPullDelta = 0
    return
  }
  if (e.deltaY < 0) {
    wheelPullDelta += Math.abs(e.deltaY)
    if (wheelPullDelta >= WHEEL_REFRESH_DELTA) {
      wheelPullDelta = 0
      refreshMessages()
    }
  } else {
    wheelPullDelta = 0
  }
}

const handleVisibleChange = val => {
  emit('update:visible', val)
}

const handleClose = () => {
  emit('update:visible', false)
}

watch(
  () => props.visible,
  async val => {
    if (val) {
      resetState()
      await fetchMessages({ autoLoadMore: true })
      await nextTick()
      setupBottomObserver()
    } else {
      teardownBottomObserver()
    }
  }
)

onBeforeUnmount(() => {
  teardownBottomObserver()
})
</script>

<style scoped lang="scss">
.record-list {
  position: relative;
  height: 520px;
  overflow-y: auto;
  padding-right: 4px;
}

.refresh-overlay {
  position: absolute;
  top: 8px;
  left: 0;
  right: 0;
  z-index: 2;
  text-align: center;
  font-size: 13px;
  color: #999;
  pointer-events: none;
}

.record-item {
  display: flex;
  gap: 10px;
  padding: 12px 0;
  border-bottom: 1px solid #f7f7f7;

  &:last-child {
    border-bottom: none;
  }

  .avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    object-fit: cover;
  }

  .record-body {
    flex: 1;
    min-width: 0;
  }

  .record-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;

    .sender {
      font-size: 14px;
      color: #838383;
    }

    .time {
      font-size: 13px;
      color: #bfbfbf;
    }
  }
}

.load-more-tip,
.empty-tip {
  text-align: center;
  font-size: 13px;
  color: #999;
  padding: 12px 0;
}

.scroll-sentinel {
  height: 1px;
}
</style>

<style lang="scss">
.enterprise-wechat-record-modal {
  .ant-modal-header {
    padding: 17px 24px;
    border-bottom: 1px solid #f2f2f2;
  }

  .ant-modal-title {
    line-height: 1.4;
  }

  .modal-title {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    flex-wrap: wrap;
  }

  .modal-title-main {
    font-weight: 500;
    font-size: 16px;
    color: #333;
    line-height: 22px;
  }

  .modal-title-range {
    font-weight: 400;
    font-size: 14px;
    color: #666;
    line-height: 20px;
  }

  .ant-modal-body {
    padding: 0 24px 16px;
  }
}
</style>
